/**
 * Favorites operations against `/favorites.php` and `/gallerypopups.php`.
 *
 * The same endpoints exist on both e-hentai and exhentai; only the host
 * differs. EhViewer documents the exact form bodies:
 *
 *   add:    `favcat=N&favnote=...&apply=Apply+Changes&update=1`
 *   delete: `favcat=favdel&favnote=&apply=Apply+Changes&update=1`
 *
 * The endpoint always requires a matching `Referer` for the active site.
 *
 * `listFavorites` returns both the page of cards and the user's 10 native
 * category names so the UI can render proper tabs.
 */

import type { ComicCard, Page } from '@/sources/types';
import type { ProxyFetchOpts } from '@/lib/proxy';
import { NetworkError } from '@/lib/errors';
import { buildFavoritesUrl } from './list-url';
import { fetchEhText } from './network';
import { ENDPOINT_GALLERY_POPUPS, withNw, type SiteContext } from './urls';
import { parseGalleryList } from './parsers/gallery-list';
import { parseFavoritesHeader, type FavoritesHeader } from './parsers/favorites';

export interface ListFavoritesArgs {
  /** 0..9 = specific favcat; undefined or -1 = "All Favorites". */
  category?: number;
  search?: string;
  /** 0-indexed page. */
  page?: number;
  /** Newer e-hentai pagination cursor from `next=` links. */
  cursor?: string;
}

export interface ListFavoritesResult {
  page: Page<ComicCard>;
  /** The 10 native categories with their user-customized names + counts. */
  header: FavoritesHeader;
}

/**
 * Fetch `/favorites.php?favcat=N&...` and parse both the list and the
 * favcat dropdown. The category list always has exactly 10 entries.
 */
export async function listFavorites(
  site: SiteContext,
  auth: { cookies?: string },
  args: ListFavoritesArgs = {},
): Promise<ListFavoritesResult> {
  const urlOpts: { category?: number; keyword?: string; page?: number } = {};
  if (args.category !== undefined) urlOpts.category = args.category;
  if (args.search !== undefined) urlOpts.keyword = args.search;
  if (args.page !== undefined) urlOpts.page = args.page;
  const url = buildFavoritesUrl(site, urlOpts);
  if (args.cursor) {
    const withCursor = new URL(url);
    withCursor.searchParams.delete('page');
    withCursor.searchParams.set('next', args.cursor);
    return fetchFavoritesPage(site, auth, args, withNw(withCursor.toString(), site));
  }
  return fetchFavoritesPage(site, auth, args, url);
}

async function fetchFavoritesPage(
  site: SiteContext,
  auth: { cookies?: string },
  args: ListFavoritesArgs,
  url: string,
): Promise<ListFavoritesResult> {
  const fetchOpts: { cookies?: string } = {};
  if (auth.cookies) fetchOpts.cookies = auth.cookies;
  const text = await fetchEhText(url, site, fetchOpts);
  const header = parseFavoritesHeader(text);
  const page = parseGalleryList(text, {
    source: site.id,
    baseUrl: site.host,
    currentPage: args.page ?? 0,
  });
  return { page, header };
}

/**
 * Build a properly-encoded form body for the `addfav` / `favdel` POST.
 * Exposed for tests — the same builder is used internally by
 * {@link addFavorite} and {@link removeFavorite}.
 */
export function buildFavoriteFormBody(args: { favcat: number | 'favdel'; note?: string }): string {
  const params = new URLSearchParams();
  params.set('favcat', String(args.favcat));
  params.set('favnote', args.note ?? '');
  params.set('apply', 'Apply Changes');
  params.set('update', '1');
  return params.toString();
}

function popupsUrl(site: SiteContext, gid: number | string, token: string): string {
  const u = new URL(`${site.host}${ENDPOINT_GALLERY_POPUPS}`);
  u.searchParams.set('gid', String(gid));
  u.searchParams.set('t', token);
  u.searchParams.set('act', 'addfav');
  return u.toString();
}

async function postFavoriteMutation(
  site: SiteContext,
  gid: number | string,
  token: string,
  body: string,
  cookies: string,
): Promise<void> {
  const url = popupsUrl(site, gid, token);
  const headers: HeadersInit = {
    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    Accept: 'text/html, */*; q=0.01',
    Origin: site.host,
    Referer: site.host + '/',
    'X-Requested-With': 'XMLHttpRequest',
  };
  const fetchOpts: ProxyFetchOpts = {
    method: 'POST',
    headers,
    body,
  };
  if (cookies) fetchOpts.cookies = cookies;
  const text = await fetchEhText(url, site, fetchOpts);
  // Keep a defensive guard in case the upstream login marker changes before typed detection.
  if (/Login Form/i.test(text) && /act=Login/i.test(text)) {
    throw new NetworkError(`favorites POST returned login page (${site.id})`, {
      source: site.id,
    });
  }
}

/**
 * Add a gallery to a specific favorite category (0..9). The user can also
 * attach a free-form note; we forward it verbatim.
 */
export async function addFavorite(
  site: SiteContext,
  args: { gid: number | string; token: string; category: number; note?: string },
  auth: { cookies?: string },
): Promise<void> {
  if (args.category < 0 || args.category > 9) {
    throw new Error(`addFavorite: category must be 0..9 (got ${args.category})`);
  }
  const formArgs: { favcat: number; note?: string } = { favcat: args.category };
  if (args.note !== undefined) formArgs.note = args.note;
  const body = buildFavoriteFormBody(formArgs);
  await postFavoriteMutation(site, args.gid, args.token, body, auth.cookies ?? '');
}

/** Remove a gallery from favorites entirely (server-side). */
export async function removeFavorite(
  site: SiteContext,
  args: { gid: number | string; token: string },
  auth: { cookies?: string },
): Promise<void> {
  const body = buildFavoriteFormBody({ favcat: 'favdel' });
  await postFavoriteMutation(site, args.gid, args.token, body, auth.cookies ?? '');
}
